import _ from 'lodash';
/**
 * Service to manage the current user information
 *
 * @class UserInfoService
 * @name UserInfoService
 */
/* @ngInject */
export default class UserInfoService {
    // User kind constants
    static NORMAL = 'normal';
    static ADMIN = 'admin';
    static PARTNER = 'partner';
    static SUPER_ADMIN = 'superAdmin';
    // User kinds in order of privilege
    static USER_KINDS = [
        UserInfoService.NORMAL,
        UserInfoService.ADMIN,
        UserInfoService.PARTNER,
        UserInfoService.SUPER_ADMIN
    ];
    $sails;
    $log;
    appConfig;
    constructor($sails, $log, appConfig) {
        this.$sails = $sails;
        this.$log = $log;
        this.appConfig = appConfig;
    }
    // Instance methods to access constants (for AngularJS compatibility)
    /**
     * Returns the USER_KINDS array for use in JavaScript components.
     *
     * ⚠️ WHY NOT USE STATIC PROPERTIES DIRECTLY?
     *
     * Static properties (UserInfoService.USER_KINDS) are NOT accessible when this
     * service is injected into AngularJS components because:
     *
     * 1. AngularJS Dependency Injection injects SERVICE INSTANCES, not class constructors
     * 2. Static properties belong to the CLASS CONSTRUCTOR, not the instance
     * 3. When you inject 'UserInfoService', you get: new UserInfoService(...)
     * 4. The injected instance does NOT have access to static properties
     *
     * From AngularJS official docs (https://docs.angularjs.org/guide/di):
     * "Services are registered to modules via the Module API. Typically you use the
     * Module factory API to register a service: the factory function generates the
     * single object or function that represents the service to the rest of the application."
     *
     * AngularJS creates a SERVICE INSTANCE and injects that instance, not the class itself.
     *
     * SOLUTION: Use instance methods to expose static constants to JavaScript components.
     * This maintains the single source of truth while ensuring compatibility with AngularJS DI.
     *
     * @returns {UserKind[]} Array of user kinds in order of privilege
     */
    getUserKinds() {
        return UserInfoService.USER_KINDS;
    }
    /**
     * Returns individual user kind constants as an object.
     *
     * @returns {object} Object containing all user kind constants
     */
    getUserKindConstants() {
        return {
            NORMAL: UserInfoService.NORMAL,
            ADMIN: UserInfoService.ADMIN,
            PARTNER: UserInfoService.PARTNER,
            SUPER_ADMIN: UserInfoService.SUPER_ADMIN
        };
    }
    async refreshUserFromServer() {
        const { data: user } = await this.$sails.get('/user/me')
            .catch((err) => {
            this.$log.error(err);
            throw err;
        });
        return _.merge(this.appConfig.user, user);
    }
    updateConnectionLog() {
        this.$sails.post('/userlog', {}).catch(this.$log.error);
    }
    getCurrentUser() {
        return this.appConfig.user;
    }
    isCurrentUserSuperAdmin() {
        return this.appConfig.user.superAdmin;
    }
    isCurrentUserPartner() {
        return this.appConfig.user.partner;
    }
    isCurrentUserAdmin() {
        return this.appConfig.user.superAdmin ||
            this.appConfig.user.partner ||
            this.appConfig.user.admin;
    }
    isCurrentUserExactlyAdmin() {
        return this.appConfig.user.kind === UserInfoService.ADMIN;
    }
    /**
     * Returns the current user's privilege level as one of:
     * 'superAdmin', 'partner', 'admin', or 'normal'.
     *
     * @returns {UserKind}
     */
    getCurrentUserKind() {
        if (this.isCurrentUserSuperAdmin()) {
            return UserInfoService.SUPER_ADMIN;
        }
        if (this.isCurrentUserPartner()) {
            return UserInfoService.PARTNER;
        }
        if (this.isCurrentUserAdmin()) {
            return UserInfoService.ADMIN;
        }
        return UserInfoService.NORMAL;
    }
    getUserPermissions() {
        return this.getCurrentUser().permissions || {};
    }
    hasGroupedBoardsViewEnabled() {
        return Boolean(this.appConfig.user?.settings?.groupedBoardsView);
    }
}
